import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { initializeScrollAnimationTrigger } from '@/scripts/theme/animations'
import { FacetFiltersForm } from '@/scripts/catalog/facet-filters-form'

// todo: make previous page request in parallel, caching, limits with exceptions, loading indicator, "load previous page" button

export class DynamicCollection extends UcoastEl {
	static htmlSelector = 'dynamic-collection'
	sectionId: string
	isLoading = false
	hasMorePages = true
	observer?: IntersectionObserver

	constructor() {
		super()
		this.sectionId = q.ra(this, 'data-section-id')

		const currentPage = this.getCurrentPage()
		if(currentPage > 1) this.fetchPreviousPages(currentPage)

		this.setupInfiniteScroll()
		document.addEventListener('filter:updated', this.handleFilterUpdate)
		window.addEventListener('load', () => this.setupInfiniteScroll())
		window.addEventListener('popstate', () => this.handleFilterUpdate())
	}

	handleFilterUpdate = () => {
		this.hasMorePages = true
		this.isLoading = false
		this.setupInfiniteScroll()
	}

	setupInfiniteScroll() {
		this.observer?.disconnect()

		const sentinel = this.querySelector('[data-scroll-sentinel]')
		if (!sentinel) return

		this.observer = new IntersectionObserver(
			(entries) => {
				const entry = entries[0]
				if (!entry.isIntersecting) return
				if (this.isLoading || !this.hasMorePages) return

				this.loadMore()
			},
			{
				rootMargin: '0px 0px 400px 0px'
			}
		)

		this.observer.observe(sentinel)
	}

	getCurrentPage() {
		const searchParams = new URLSearchParams(window.location.search)
        return parseInt(searchParams.get('page') || '1', 10)
	}

	async fetchPreviousPages(currentPage: number) {
		if (this.isLoading) return
		this.isLoading = true
		document.body.classList.add('loading')

		this.querySelector('.collection__item')?.classList.add('first-load')
		const existingGrid = this.querySelector('#product-grid')
		if (!existingGrid) return

		try {
			const results = []
			for (let page = 1; page <= currentPage; page++) {
				const url = this.getUrlForPage(page)
				const response = await fetch(url)
				if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
				const html = await response.text()
				results.push(html)
			}

			existingGrid.innerHTML = ''
			results.forEach((html, index) => {
				const dom = new DOMParser().parseFromString(html, 'text/html')
				const productGrid = dom.querySelector('#product-grid')
				if (!productGrid) return

				existingGrid.innerHTML = existingGrid.innerHTML + productGrid.innerHTML
				if(index == results.length - 1) {
					this.querySelector(`.collection__item:nth-child(${this.dataset.productsPerPage * (currentPage - 1) + 1})`)?.scrollIntoView({
						behavior: 'smooth',
						block: 'nearest',
						inline: 'start'
					})
				}
			})

			// initializeScrollAnimationTrigger(existingGrid)
		} catch (error) {
			console.error('Error loading previous pages:', error)
		} finally {
			this.isLoading = false
			document.body.classList.remove('loading')
		}
	}

	getUrlForPage(page: number) {
		const sections = FacetFiltersForm.getSections()
		const currentSearch = new URLSearchParams(window.location.search)
		currentSearch.set('page', page.toString())
		// if(!currentSearch.get('filter.v.availability')) currentSearch.set('filter.v.availability', '1')
		return `${window.location.pathname}?section_id=${sections[0].section}&${currentSearch.toString()}`
	}

	async loadMore() {
		if (this.isLoading || !this.hasMorePages) return
		this.isLoading = true

		const nextPage = this.getCurrentPage() + 1
		const url = this.getUrlForPage(nextPage)
		this.classList.add('loading')

		try {
			const response = await fetch(url)
			if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
			const html = await response.text()

			const dom = new DOMParser().parseFromString(html, 'text/html')
			const productGrid = dom.querySelector('#product-grid')
			const existingGrid = this.querySelector('#product-grid')

			if (!productGrid || !existingGrid) return

			const newProducts = productGrid.querySelectorAll('.collection__item')
			if (newProducts.length === 0) {
				this.hasMorePages = false
				return
			}

			existingGrid.innerHTML = existingGrid.innerHTML + productGrid.innerHTML

			// initializeScrollAnimationTrigger(existingGrid)

			if (!dom.querySelector('[data-scroll-sentinel]')) {
				this.hasMorePages = false
			}

			const searchParams = new URLSearchParams(window.location.search)
			searchParams.set('page', nextPage.toString())
			FacetFiltersForm.updateURLHash(searchParams.toString())
			FacetFiltersForm.searchParamsPrev = searchParams.toString()

		} catch (error) {
			console.error('Error loading more products:', error)
			this.hasMorePages = false
		} finally {
			this.isLoading = false
			this.classList.remove('loading')
		}
	}

	override disconnectedCallback() {
		this.observer?.disconnect()
		document.removeEventListener('filter:updated', this.handleFilterUpdate)
		window.removeEventListener('popstate', this.handleFilterUpdate)
	}
}